// LLM generated - Model Claude 4 Sonnet
import { useMutation } from '@tanstack/react-query';
import { showNotification } from '@mantine/notifications';
import useTranslation from 'next-translate/useTranslation';

import { postWalletRefund } from '~/services/wallet/wallet.service.api';
import type { IPostWalletsRefundsPayload } from '~/services/wallet/interfaces/wallets.interface';
import { reportEvent } from '~/domains/analytics';
import { logError } from '~/common/utils/log';

import { useWalletBalance } from './useWalletBalance';
import { useWalletRefundableBreakdown } from './useWalletRefundableBreakdown';

type UseSubmitRefundProps = {
  onSuccess?: () => void;
  onSuccessWithRemainingRefunds?: () => void;
};

export function useSubmitRefund({
  onSuccess,
  onSuccessWithRemainingRefunds,
}: UseSubmitRefundProps = {}) {
  const { t } = useTranslation('payments');
  const { refetch: refetchWalletBalance } = useWalletBalance();
  const { refetch: refetchRefundableBreakdown } = useWalletRefundableBreakdown();

  const mutation = useMutation({
    mutationFn: (payload: IPostWalletsRefundsPayload) => postWalletRefund(payload),
    onSuccess: async (response, payload) => {
      // Refresh balance data (like deposits and withdrawals do)
      refetchWalletBalance();

      // Wait for the refundable breakdown refetch to complete and get fresh data
      const freshRefundableBreakdown = await refetchRefundableBreakdown();

      // Track analytics (like deposits and withdrawals do)
      reportEvent('Transactions > Refund', {
        refund_id: response?.id || 'unknown',
        refund_amount: payload.amount,
        refund_method: payload.paymentMethod,
        currency: 'USD',
      });

      // Check if there are remaining refundable items using fresh data
      const hasRemainingRefunds =
        freshRefundableBreakdown?.data?.items?.some((item) => item.refundableBalance > 0) ?? false;

      if (hasRemainingRefunds) {
        // Show success message for remaining refunds scenario
        showNotification({
          title: t('refund.success.title'),
          message: t('refund.success.messageWithRemaining'),
          color: 'green',
        });
        onSuccessWithRemainingRefunds?.();
      } else {
        // Show success message for complete refund scenario
        showNotification({
          title: t('refund.success.title'),
          message: t('refund.success.message'),
          color: 'green',
        });
        onSuccess?.();
      }
    },
    onError: (error) => {
      logError(error);
      showNotification({
        title: t('refund.error.title'),
        message: t('refund.error.message'),
        color: 'red',
      });
    },
  });

  return {
    submitRefund: mutation.mutate,
    isSubmitting: mutation.isLoading,
    error: mutation.error,
  };
}
